import { _ as _extends } from '../../_rollupPluginBabelHelpers-mO86zOh3.js';
import React from 'react';
import { useDebounce } from '../../hooks/useDebounce.js';
import { jsx } from '@emotion/react';
import { Text } from '../Text/Text.js';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden.js';
import '@indeed/ifl-css';
import '../../utils/createResponsive.js';

const defaultFormatter = (val, lim) => jsx("span", {
  dir: "ltr"
}, val, " / ", lim);

/**
 * @see https://experience.indeed.design/components/ids/character-counter
 */
const CharacterCounter = _ref => {
  let {
    value = 0,
    limit,
    formatter = defaultFormatter,
    id,
    sx,
    ...props
  } = _ref;
  // Logic of what format to display is set by the following table https://link.indeed.tech/388KRK
  const formattedContent = formatter(value, limit);
  let formattedVisualValue = defaultFormatter(value, limit);
  let formattedScreenReaderValue = defaultFormatter(value, limit);

  // If the user sent in formatter isn't a supported format, then fall back to the default
  if (/^(object|string)$/.test(typeof formattedContent)) {
    // If the formatter uses the legacy format of specifying a single ReactNode or string, use it for both visual & screen reader
    if (/*#__PURE__*/React.isValidElement(formattedContent) || typeof formattedContent === "string") {
      formattedVisualValue = formattedContent;
      formattedScreenReaderValue = formattedContent;
    } else {
      const formattedObject = formattedContent;
      const hasVisualProp = Object.prototype.hasOwnProperty.call(formattedObject, "visual");
      const hasSroProp = Object.prototype.hasOwnProperty.call(formattedObject, "screenReaderOnly");
      if (hasVisualProp) {
        formattedVisualValue = formattedObject.visual;
      }
      if (hasSroProp) {
        formattedScreenReaderValue = formattedObject.screenReaderOnly;
      } else if (hasVisualProp) {
        formattedScreenReaderValue = formattedObject.visual;
      }
    }
  }

  // Debounce visually hidden character counter so it doesn't conflict with
  // other screen reader announcements.
  const debouncedVal = useDebounce(formattedScreenReaderValue, 3000);
  return jsx(React.Fragment, null, jsx(Text, _extends({
    "aria-hidden": "true",
    level: 2,
    sx: sx
  }, props), formattedVisualValue), jsx(VisuallyHidden, {
    id: id,
    "aria-live": "polite",
    "aria-atomic": "true"
  }, debouncedVal));
};
CharacterCounter.displayName = "CharacterCounter";

export { CharacterCounter };
